<template>
  <ADrawer
    :width="800"
    :open="open"
    class="company-editing-drawer report-revision-drawer"
    :body-style="{ paddingBottom: '24px' }"
    :header-style="{ padding: '32px 40px 32px 40px' }"
    :footer-style="{ textAlign: 'left', padding: '20px 40px 40px 40px' }"
    destroy-on-close
    @close="emit('update:open', false)"
  >
    <template #title>
      <div class="report-revision-drawer__header">
        <h3 class="report-revision-drawer__title">Вернуть на доработку</h3>
        <p class="report-revision-drawer__subtitle">Укажите причину возврата отчета на доработку</p>
      </div>
    </template>

    <template #closeIcon>
      <span class="company-editing-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div class="report-revision-drawer__sections">
      <section class="report-revision-drawer__section">
        <span class="report-revision-drawer__section-label">1. Причина</span>

        <div class="report-revision-drawer__section-content">
          <AForm layout="vertical" class="report-revision-drawer__form">
            <AFormItem
              label="Комментарий"
              class="stc-ant-field-skin report-revision-drawer__field"
              required
            >
              <ATextarea
                v-model:value="comment"
                placeholder="Причина возврата отчета"
                :maxlength="COMMENT_MAX_LENGTH"
                :auto-size="{ minRows: 6, maxRows: 10 }"
                show-count
              />
            </AFormItem>
          </AForm>
        </div>
      </section>

      <a-divider class="report-revision-drawer__divider" />

      <section class="report-revision-drawer__section">
        <span class="report-revision-drawer__section-label">2. Документы</span>

        <div class="report-revision-drawer__section-content">
          <FormFileUpload ref="fileUploadRef" v-model="fileItems" />
        </div>
      </section>
    </div>

    <template #footer>
      <ARow align="middle" justify="end" class="company-editing-drawer__toolbar">
        <ACol>
          <ASpace>
            <button
              type="button"
              class="btn btn-secondary btn-medium"
              :disabled="saving"
              @click="emit('update:open', false)"
            >
              Отмена
            </button>
            <button
              type="button"
              class="btn btn-primary btn-medium"
              :disabled="saving || !canSubmit"
              @click="handleSubmit"
            >
              {{ saving ? 'Отправка...' : 'Отправить' }}
            </button>
          </ASpace>
        </ACol>
      </ARow>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { showStcWarningNotification } from '@/js/components/stc-notification';
import {
  Col as ACol,
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Row as ARow,
  Space as ASpace,
  Textarea as ATextarea,
} from 'ant-design-vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';

const COMMENT_MAX_LENGTH = 512;

const props = defineProps<{
  open: boolean;
  saving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'submit', payload: { comment: string; files: File[] }): void;
}>();

const comment = ref('');
const fileItems = ref<FileUploadItem[]>([]);
const fileUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);

const canSubmit = computed(() => comment.value.trim().length > 0);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      return;
    }

    comment.value = '';
    fileItems.value = [];
    fileUploadRef.value?.reset();
  },
);

function handleSubmit(): void {
  if (!canSubmit.value || props.saving) {
    return;
  }

  const readyFiles = fileUploadRef.value?.getReadyFiles() ?? [];
  const hasUploadingFiles = fileItems.value.some((item) => item.status === 'uploading');

  if (hasUploadingFiles) {
    showStcWarningNotification('Дождитесь окончания загрузки файлов');
    return;
  }

  emit('submit', {
    comment: comment.value.trim(),
    files: readyFiles,
  });
}
</script>
